import type { ZudokuConfig } from "zudoku";
import { LandingPage } from "./src/LandingPage";

const LOGO_LIGHT_URL = "https://storage.googleapis.com/zero-cdn-public-files-production-0bkbt/assets/logos/strictly/logo-color.png";
const LOGO_DARK_URL = "https://storage.googleapis.com/zero-cdn-public-files-production-0bkbt/assets/logos/strictly/logo-white.png";

const config: ZudokuConfig = {
  metadata: {
    favicon: LOGO_LIGHT_URL,
    title: "Strictly Developer Portal",
    description:
      "Documentation and API reference for the Strictly Payment Platform.",
  },

  site: {
    title: "Strictly Developer Portal",
    logo: {
      src: {
        light: LOGO_LIGHT_URL,
        dark: LOGO_DARK_URL,
      },
      alt: "Strictly",
      width: 32,
      href: "/",
    },
    banner: {
      message: (
        <span>
          Need a sandbox account?{" "}
          <a href="https://strictlyzero.com/contact/" target="_blank" rel="noopener noreferrer">
            Contact us
          </a>{" "}
          to get your merchant credentials and start testing.
        </span>
      ),
      dismissible: true,
    },
    footer: {
      columns: [
        {
          title: "Get Started",
          links: [
            { label: "Introduction", href: "/introduction" },
            { label: "Quick Start", href: "/getting-started" },
            { label: "Authentication", href: "/authentication" },
            { label: "Sandbox & Testing", href: "/sandbox" },
          ],
        },
        {
          title: "Integration Guide",
          links: [
            { label: "Tokenization", href: "/tokenization" },
            { label: "Surcharge Payments", href: "/surcharge-payments" },
            { label: "Customer Vault", href: "/customer-vault" },
            { label: "Pagination & Filtering", href: "/pagination" },
            { label: "Code Examples", href: "/code-examples" },
          ],
        },
        {
          title: "Reference",
          links: [
            { label: "Error Handling", href: "/errors" },
            { label: "Webhooks", href: "/webhooks" },
            { label: "Best Practices", href: "/best-practices" },
            { label: "API Reference", href: "/api" },
          ],
        },
      ],
      copyright: `© ${new Date().getFullYear()} Strictly. All rights reserved.`,
    },
  },

  navigation: [
    {
      type: "custom-page",
      path: "/",
      element: <LandingPage />,
    },
    {
      type: "category",
      label: "Documentation",
      icon: "book-open",
      items: [
        "introduction",
        { type: "separator" },
        { type: "section", label: "Get Started" },
        {
          type: "doc",
          file: "getting-started",
          label: "Quick Start",
          icon: "zap",
        },
        {
          type: "doc",
          file: "authentication",
          label: "Authentication",
          icon: "lock",
        },
        {
          type: "doc",
          file: "sandbox",
          label: "Sandbox & Testing",
          icon: "flask-conical",
        },
        { type: "separator" },
        { type: "section", label: "Integration Guide" },
        {
          type: "doc",
          file: "tokenization",
          label: "Tokenization",
          icon: "shield",
        },
        {
          type: "doc",
          file: "surcharge-payments",
          label: "Surcharge Payments",
          icon: "percent",
        },
        {
          type: "doc",
          file: "customer-vault",
          label: "Customer Vault",
          icon: "users",
        },
        {
          type: "doc",
          file: "pagination",
          label: "Pagination & Filtering",
          icon: "list",
        },
        { type: "separator" },
        { type: "section", label: "API Concepts" },
        {
          type: "doc",
          file: "errors",
          label: "Error Handling",
          icon: "alert-circle",
        },
        {
          type: "doc",
          file: "webhooks",
          label: "Webhooks",
          icon: "webhook",
        },
        {
          type: "doc",
          file: "best-practices",
          label: "Best Practices",
          icon: "circle-check",
        },
        { type: "separator" },
        {
          type: "doc",
          file: "code-examples",
          label: "Code Examples",
          icon: "code-2",
        },
        { type: "separator" },
        {
          type: "link",
          label: "API Reference →",
          to: "/api",
          icon: "arrow-right",
        },
      ],
    },
    {
      type: "link",
      icon: "code-2",
      to: "/api",
      label: "API Reference",
    },
  ],

  apis: {
    type: "file",
    input: "./strictly-api.openapi.yaml",
    path: "/api",
    categories: [
      { label: "Core Payments", tags: ["Payments", "ACH", "Transactions"] },
      {
        label: "Customers",
        tags: ["Customers", "Tokenize"],
      },
      {
        label: "Billing",
        tags: ["Subscriptions", "Plans", "Invoices", "Items", "Taxes"],
      },
      {
        label: "Payment Links",
        tags: ["Payment Links", "Payment Requests"],
      },
      {
        label: "Operations",
        tags: ["Terminal", "Reports", "Webhooks"],
      },
      { label: "POS Lending", tags: ["Public API — POS Lending (Avvance)"] },
    ],
  },

  defaults: {
    apis: {
      schemaDownload: { enabled: true },
    },
  },

  theme: {
    // Zudoku renders either the logo or the site title, never both.
    // Append the title next to the logo so the brand name stays visible.
    customCss: {
      'header a[href="/"] > div::after': {
        content: '"Strictly Developer Portal"',
        "font-weight": "600",
        "font-size": "1.25rem",
        "white-space": "nowrap",
      },
    },
    light: {
      primary: "221 83% 53%",
      primaryForeground: "0 0% 98%",
      background: "0 0% 100%",
      foreground: "222 47% 11%",
      card: "0 0% 100%",
      cardForeground: "222 47% 11%",
      muted: "210 40% 96%",
      mutedForeground: "215 16% 47%",
      border: "214 32% 91%",
    },
    dark: {
      primary: "217 91% 60%",
      primaryForeground: "222 47% 11%",
      background: "222 47% 5%",
      foreground: "213 31% 91%",
      card: "222 47% 8%",
      cardForeground: "213 31% 91%",
      muted: "222 47% 11%",
      mutedForeground: "215 20% 55%",
      border: "216 34% 17%",
    },
  },
};

export default config;
